import { type POSSIBLE_METRICS_ENUM } from "constants/metrics"
import { ChartLine, GridNine, TrendUp } from "@phosphor-icons/react"
import { useQuery } from "@tanstack/react-query"
import { toDictionary } from "@tokenterminal/core/utils/toDictionary"
import { Box, type BoxProps, Row } from "@tokenterminal/ui/Box"
import { ScrollBox } from "@tokenterminal/ui/ScrollBox"
import { withOverflow } from "@tokenterminal/ui/util.css"
import {
  getMarketIcon,
  DashboardIcon,
  getMetricIcon,
} from "components/DashboardIcon"
import { useAuth } from "context/AuthContext"
import useMarketSectors from "hooks/trpc/useMarketSectors"
import {
  EXPERIMENTAL_FLAGS,
  useExperimentalFlagEnabled,
} from "hooks/useExperimentalFlags"
import { useIsMac } from "hooks/useIsMac"
import { useDashboards } from "modules/discover/hooks/useDashboards"
import { toSlug } from "modules/market-sectors/utils"
import { useProjectsMeta } from "modules/project/hooks/useMeta"
import { useProductsMeta } from "modules/projects/hooks/useProductsMeta"
import Image from "next/image"
import {
  type ChangeEvent,
  forwardRef,
  type HTMLAttributes,
  type KeyboardEventHandler,
  type ReactNode,
  type Ref,
  startTransition,
  Suspense,
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import { twMerge } from "tailwind-merge"
import { getProjectPageRoute } from "utils/getProjectPageRoute"
import { useTRPC } from "utils/trpc"
import { getSidebarIcon, SIDEBAR_ICON } from "../NavIcon"
import { navigationDocsItems, navigationItems } from "../Navigation.config"
import {
  searchInputStyle,
  searchInputWrapper,
  searchModalItems,
  searchContainer,
} from "./Search.css"
import { filterItems } from "./Search.utils"
import SearchResult from "./SearchResults"
import SearchIcon from "@/icons/search.svg"
type SearchMetric = {
  name: string
  id: string
  metricId: POSSIBLE_METRICS_ENUM
}

const listId = "project-list"

const SearchModalHeading = ({
  className,
  ...props
}: HTMLAttributes<HTMLHeadingElement>) => (
  <h2 className={twMerge("font-medium ml-4", className)} {...props} />
)

const SearchInput = forwardRef<HTMLInputElement, Omit<BoxProps, "children">>(
  (props, ref) => (
    <Box
      ref={ref}
      as="input"
      data-is-mac={useIsMac()}
      data-has-value={Boolean(props.value)}
      type="search"
      className={searchInputStyle}
      {...props}
    />
  )
)

const ResultListStack = ({
  children,
  title,
}: {
  children: ReactNode
  title: string
}) => (
  <div className="flex flex-col gap-2 max-w-full">
    <div className="ml-4 font-medium text-sm text-fg-secondary">{title}</div>
    <ol className="gap-2 flex flex-col">{children}</ol>
  </div>
)

SearchInput.displayName = "SearchInput"

const searchNavItems = navigationItems
  .map(({ title, href, iconType }) => ({
    name: title,
    id: href,
    iconType,
  }))
  .concat(
    navigationDocsItems.map(({ title, href, iconType }) => ({
      name: title,
      id: href,
      iconType,
    }))
  )

interface TerminalSearchProps {
  closeSearch: () => void
}

const SearchResultList = forwardRef<
  HTMLDivElement,
  {
    searchValue: string
    linkRef: Ref<HTMLAnchorElement>
    onNavigate: () => void
    cursor: number
  }
>(function SearchResultList(
  { searchValue, linkRef, onNavigate, cursor },
  listRef
) {
  const trpc = useTRPC()
  const { isLoggedIn } = useAuth()
  const deferredSearchValue = useDeferredValue(searchValue)
  const projectsData = useProjectsMeta()
  const { data: searchMarkets } = useMarketSectors()
  const { data: { data: metricsMeta = [] } = {} } = useQuery(
    trpc.metrics.getMetricsConfiguration.queryOptions()
  )
  const metricsConfigurationsBySlug = useMemo(() => {
    return toDictionary(metricsMeta, "slug")
  }, [metricsMeta])
  const productsMeta = useProductsMeta()
  const searchDashboards = useDashboards()

  const { enabled: newProductPageEnabled } = useExperimentalFlagEnabled(
    EXPERIMENTAL_FLAGS.PRODUCT_PAGES_V1
  )

  const isLoading = !projectsData.length

  const items = useMemo(() => {
    // updateSearchParam(deferredSearchValue)

    if (!deferredSearchValue) {
      const items = searchNavItems.concat(
        ...(isLoggedIn
          ? [
              {
                name: "Studio",
                id: "/studio/dashboards",
                iconType: SIDEBAR_ICON.Studio,
              },
            ]
          : [])
      )

      return {
        navItems: items,
        count: items.length,
      }
    }

    // SEARCH ONLY
    const filteredProjects = filterItems(
      {
        items: projectsData.map(({ slug: id, name, symbol, logo, is_new }) => ({
          // This resolved is mainly for keeping stored data low by removing un-used props
          id,
          name,
          symbol,
          logo,
          is_new,
        })),
        tags: ["project"],
        keys: ["symbol", "name", "id"],
      },
      deferredSearchValue
    )

    const metrics = filterItems(
      {
        items: metricsMeta
          // Temporary hack to show ecosystem metrics. Should remove this once
          // datahub is updated and ecosystem metrics are dashboard_enabled.
          .filter(
            (metric) =>
              metric.dashboard_enabled ||
              metric.title.toLowerCase().includes("ecosystem") ||
              metric.title === "GDP"
          )
          .map(({ metric_id, title, slug }) => ({
            name: title,
            id: slug, // components uses this as url path
            metricId: metric_id,
          })),
        tags: ["metric"],
        keys: ["name", "id"],
      },
      deferredSearchValue
    )

    const markets = filterItems(
      {
        items: searchMarkets?.data ?? [],
        tags: ["sector", "market"],
        keys: ["name", "slug"],
      },
      deferredSearchValue
    )

    const products = newProductPageEnabled
      ? filterItems(
          {
            items: (productsMeta.data ?? []).map(
              ({ product_id, project_slug, product_name, logo, symbol }) => ({
                productId: product_id,
                name: product_name,
                projectSlug: project_slug,
                logo,
                symbol,
              })
            ),
            tags: ["product"],
            keys: ["name", "projectSlug", "symbol"],
          },
          deferredSearchValue
        )
      : []

    const navItems = filterItems(
      {
        items: searchNavItems.concat(
          ...(isLoggedIn
            ? [
                {
                  name: "Studio",
                  id: "/studio/dashboards/",
                  iconType: SIDEBAR_ICON.Studio,
                },
              ]
            : [])
        ),
        tags: ["goto"],
        keys: ["name"],
      },
      deferredSearchValue
    )

    const actionItems = isLoggedIn
      ? filterItems(
          {
            items: [
              {
                name: "Create chart",
                id: "/studio/charts/new",
                icon: ChartLine,
              },
              {
                name: "Create dashboard",
                id: "/studio/dashboards/new",
                icon: GridNine,
              },
            ],
            tags: ["goto"],
            keys: ["name"],
          },
          deferredSearchValue
        )
      : []

    const dashboards = filterItems(
      {
        items: searchDashboards.map((dashboard) => ({
          id: dashboard.user_dashboard_id,
          logo_url: dashboard.logo_url,
          name: dashboard.name as string,
        })),
        tags: ["dashboard"],
        keys: ["name"],
      },
      deferredSearchValue
    )

    return {
      searchResults: {
        projects: filteredProjects,
        metrics,
        markets,
        products,
        navItems,
        dashboards,
        actionItems,
      },
      count:
        filteredProjects.length +
        metrics.length +
        markets.length +
        products.length +
        navItems.length +
        actionItems.length,
    }
  }, [
    deferredSearchValue,
    projectsData,
    metricsMeta,
    searchMarkets,
    isLoggedIn,
    searchDashboards,
    productsMeta,
    newProductPageEnabled,
  ])

  const noResults = items.count < 1

  return noResults ? (
    <SearchModalHeading>
      {isLoading ? "Loading..." : "No results"}
    </SearchModalHeading>
  ) : (
    <ScrollBox
      className={[searchModalItems, withOverflow]}
      id={listId}
      width="100%"
      data-no-keyboard-selection={cursor !== -1 ? undefined : true}
    >
      <div ref={listRef} className="flex flex-col gap-6 max-w-full">
        {items?.navItems?.length ? (
          <ResultListStack title="Navigation">
            {items.navItems.map((item, i) => {
              const icon = getSidebarIcon(item.iconType)
              return (
                // Make sure we have unique key because here it's critical
                <li key={`${item.id}-${i}`}>
                  <SearchResult
                    aria-selected={i === cursor}
                    item={item}
                    href={item.id}
                    icon={
                      icon && (
                        <DashboardIcon
                          icon={icon}
                          name={item.name}
                          // These SVGs seem visually bigger than other items, so scale down a bit to make appear similarly sized
                          size={16}
                        />
                      )
                    }
                    ref={cursor === i ? linkRef : null}
                    onClick={onNavigate}
                  />
                </li>
              )
            })}
          </ResultListStack>
        ) : null}
        {/* Now list all things that REQUIRE searching for */}
        {!searchValue || !items.searchResults ? null : (
          <>
            {items.searchResults.projects.length ? (
              <ResultListStack title="Projects">
                {items.searchResults.projects.map((item, i) => (
                  // Make sure we have unique key because here it's critical
                  <li key={`${item.id}-${i}`}>
                    <SearchResult
                      aria-selected={i === cursor}
                      item={item}
                      href={`/projects/${item.id}`}
                      ref={cursor === i ? linkRef : null}
                      onClick={onNavigate}
                    />
                  </li>
                ))}
              </ResultListStack>
            ) : null}
            {items.searchResults.dashboards.length ? (
              <ResultListStack title="Dashboards">
                {items.searchResults.dashboards.map((item, i) => {
                  const adjustedIndex = i + items.searchResults.projects.length
                  // Make sure we have unique key because here it's critical
                  return (
                    <li key={`${item.id}-${i}`}>
                      <SearchResult
                        aria-selected={adjustedIndex === cursor}
                        item={item}
                        icon={
                          item.logo_url && (
                            <Image
                              width="16"
                              height="16"
                              src={item.logo_url}
                              alt={item.name}
                              style={{
                                borderRadius: "6px",
                              }}
                            />
                          )
                        }
                        href={`/studio/dashboards/${item.id}`}
                        ref={cursor === adjustedIndex ? linkRef : null}
                        onClick={onNavigate}
                      />
                    </li>
                  )
                })}
              </ResultListStack>
            ) : null}
            {items.searchResults.metrics.length ? (
              <ResultListStack title="Metrics">
                {items.searchResults.metrics.map((item, i) => {
                  const adjustedIndex =
                    i +
                    items.searchResults.projects.length +
                    items.searchResults.dashboards.length

                  const metric = metricsConfigurationsBySlug.get(
                    toSlug(item.metricId)
                  )
                  return (
                    // Make sure we have unique key because here it's critical
                    <li key={`${item.id}-${i}`}>
                      <SearchResult
                        aria-selected={adjustedIndex === cursor}
                        item={item}
                        href={`/metrics/${item.id}`}
                        icon={
                          <DashboardIcon
                            icon={metric ? getMetricIcon(metric) : TrendUp}
                            name={item.name}
                            size={18}
                          />
                        }
                        ref={cursor === adjustedIndex ? linkRef : null}
                        onClick={onNavigate}
                      />
                    </li>
                  )
                })}
              </ResultListStack>
            ) : null}
            {items.searchResults.markets.length ? (
              <ResultListStack title="Markets">
                {items.searchResults.markets.map((item, i) => {
                  const adjustedIndex =
                    i +
                    items.searchResults.projects.length +
                    items.searchResults.metrics.length +
                    items.searchResults.dashboards.length
                  return (
                    // Make sure we have unique key because here it's critical
                    <li key={`${item.id}-${i}`}>
                      <SearchResult
                        aria-selected={adjustedIndex === cursor}
                        item={item}
                        href={`/markets/${item.id}`}
                        icon={
                          <DashboardIcon
                            icon={getMarketIcon(item.id)}
                            name={item.name}
                            size={18}
                          />
                        }
                        ref={cursor === adjustedIndex ? linkRef : null}
                        onClick={onNavigate}
                      />
                    </li>
                  )
                })}
              </ResultListStack>
            ) : null}
            {items.searchResults.products.length ? (
              <ResultListStack title="Products">
                {items.searchResults.products.map((item, i) => {
                  const adjustedIndex =
                    i +
                    items.searchResults.projects.length +
                    items.searchResults.metrics.length +
                    items.searchResults.markets.length +
                    items.searchResults.dashboards.length
                  return (
                    <li key={`${item.productId}-${i}`}>
                      <SearchResult
                        aria-selected={adjustedIndex === cursor}
                        item={item}
                        href={getProjectPageRoute({
                          projectSlug: item.projectSlug,
                          productId: item.productId,
                        })}
                        ref={cursor === adjustedIndex ? linkRef : null}
                        onClick={onNavigate}
                      />
                    </li>
                  )
                })}
              </ResultListStack>
            ) : null}
            {items.searchResults.navItems.length ? (
              <ResultListStack title="Sections">
                {items.searchResults.navItems.map((item, i) => {
                  const icon = getSidebarIcon(item.iconType)
                  const adjustedIndex =
                    i +
                    items.searchResults.projects.length +
                    items.searchResults.metrics.length +
                    items.searchResults.markets.length +
                    items.searchResults.dashboards.length
                  return (
                    // Make sure we have unique key because here it's critical
                    <li key={`${item.id}-${i}`}>
                      <SearchResult
                        aria-selected={adjustedIndex === cursor}
                        item={item}
                        href={item.id}
                        icon={
                          icon && (
                            <DashboardIcon
                              icon={icon}
                              name={item.name}
                              // These SVGs seem visually bigger than other items, so scale down a bit to make appear similarly sized
                              size={16}
                            />
                          )
                        }
                        ref={cursor === adjustedIndex ? linkRef : null}
                        onClick={onNavigate}
                      />
                    </li>
                  )
                })}
              </ResultListStack>
            ) : null}
            {items.searchResults.actionItems.length ? (
              <ResultListStack title="Actions">
                {items.searchResults.actionItems.map((item, i) => {
                  const adjustedIndex =
                    i +
                    items.searchResults.projects.length +
                    items.searchResults.metrics.length +
                    items.searchResults.markets.length +
                    items.searchResults.dashboards.length +
                    items.searchResults.navItems.length

                  return (
                    <li key={`${item.id}-${i}`}>
                      <SearchResult
                        aria-selected={adjustedIndex === cursor}
                        item={item}
                        href={item.id}
                        icon={
                          <DashboardIcon
                            icon={item.icon}
                            name={item.name}
                            // These SVGs seem visually bigger than other items, so scale down a bit to make appear similarly sized
                            size={16}
                          />
                        }
                        ref={cursor === adjustedIndex ? linkRef : null}
                        onClick={onNavigate}
                      />
                    </li>
                  )
                })}
              </ResultListStack>
            ) : null}
          </>
        )}
      </div>
    </ScrollBox>
  )
})

export const Search = ({
  closeSearch,
  ...boxProps
}: BoxProps & TerminalSearchProps) => {
  const input = useRef<HTMLInputElement>(null)
  const linkRef = useRef<HTMLAnchorElement>(null)
  const listRef = useRef<HTMLDivElement>(null)
  // const { updateSearchParam } = useSearchActions()

  const [searchValue, setSearchValue] = useState<string>("") // useSearchValue() ?? "")
  const [cursor, setCursorState] = useState(-1)

  const focusInput = useCallback(() => {
    input?.current?.focus()
  }, [])

  const resetCursor = useCallback(() => {
    setCursorState(-1)
  }, [])

  const onNavigate = useCallback(() => {
    setSearchValue("")
    resetCursor()
    closeSearch?.()
    // Maybe makes sense to move focus to the content right after navigation. Seems like a favor for the user
    document?.querySelector("main")?.focus()
  }, [resetCursor, closeSearch])

  useEffect(() => {
    focusInput()
  }, [focusInput])

  useEffect(() => {
    let scrollRaf: number | undefined
    if (linkRef?.current) {
      scrollRaf = requestAnimationFrame(() => {
        if (linkRef?.current) {
          linkRef.current.scrollIntoView({
            behavior: "auto",
            block: "center",
          })
        }
      })
    }
    return () => {
      if (scrollRaf) {
        cancelAnimationFrame(scrollRaf)
      }
    }
  }, [cursor])

  const onInputKeyDown: KeyboardEventHandler<HTMLInputElement> = useCallback(
    (e) => {
      const items = Array.from(listRef.current?.querySelectorAll("li") ?? [])

      switch (e.key) {
        case "ArrowUp":
          if (cursor) {
            // Prevent default scroll effect or it prevents our own
            e.preventDefault()

            startTransition(() => {
              setCursorState((c) => Math.max(0, c - 1))
            })
          }
          break
        case "ArrowDown": {
          if (cursor < items.length) {
            // Prevent default scroll effect or it prevents our own
            e.preventDefault()

            startTransition(() => {
              setCursorState((c) => Math.min(items.length - 1, c + 1))
            })
          }
          break
        }
        case "Enter": {
          if (linkRef?.current) {
            // Prevent default to stop this from triggering other things. Without it, for some reason, the header search trigger is clicked
            e.preventDefault()

            linkRef?.current.click()
            resetCursor()
          }
          break
        }
      }
    },
    [resetCursor, cursor]
  )

  const handleChange = useCallback(
    ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
      // Never delay setting search value
      setSearchValue(value)

      startTransition(() => {
        const items = Array.from(listRef.current?.querySelectorAll("li") ?? [])

        // Auto select first row if there's only few results for search
        if (value.length > 1 || (items.length && items.length < 5)) {
          setCursorState(0)
        }
      })
    },
    []
  )

  return (
    <Box
      paddingY={["m", "l"]}
      paddingX={["sm", "l"]}
      overflow="hidden"
      className={searchContainer}
      {...boxProps}
    >
      <Row reverse className={searchInputWrapper} onClick={focusInput}>
        <SearchInput
          ref={input}
          placeholder="Search for a project, metric, market sector, dashboard..."
          onChange={handleChange}
          onBlur={resetCursor}
          onKeyDown={onInputKeyDown}
          role="combobox"
          aria-controls={listId}
          aria-expanded={!!searchValue}
          value={searchValue}
        />
        <SearchIcon role="presentation" aria-hidden="true" />
      </Row>

      {/* 100% - height of the input wrapper */}
      <section className="flex flex-col gap-2 pt-4 md:pt-6 px-2 md:px-4 max-h-[calc(100%-96px)]">
        <Suspense fallback={null}>
          <SearchResultList
            ref={listRef}
            searchValue={searchValue}
            linkRef={linkRef}
            onNavigate={onNavigate}
            cursor={cursor}
          />
        </Suspense>
      </section>
    </Box>
  )
}
